import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type DonutSegment = {
    key: string
    label: string
    value: number
    stroke: string
    fill: string
}

type StatusDonutProps = {
    segments: DonutSegment[]
    total: number
    centerValue: string
    centerLabel: string
}

const SIZE = 152
const STROKE = 16
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

// Part-to-whole at a glance: five fixed status colours with a 2px surface gap between
// segments, always paired with the labelled legend below it.
export const StatusDonut: FC<StatusDonutProps> = ({ segments, total, centerValue, centerLabel }): ReactNode => {
    const visible = segments.filter(segment => segment.value > 0)
    const gap = visible.length > 1 ? 2 : 0
    let offset = 0

    return (
        <div className='flex flex-col items-center gap-5'>
            <div
                className='relative'
                style={{ width: SIZE, height: SIZE }}
                role='img'
                aria-label={`Dari ${total} siswa: ${segments.map(segment => `${segment.value} ${segment.label.toLowerCase()}`).join(', ')}`}
            >
                <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className='-rotate-90' aria-hidden='true'>
                    <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill='none' strokeWidth={STROKE} className='stroke-muted' />

                    {total > 0 && visible.map(segment => {
                        const length = (segment.value / total) * CIRCUMFERENCE
                        const dash = Math.max(0, length - gap)
                        const circle = (
                            <circle
                                key={segment.key}
                                cx={SIZE / 2}
                                cy={SIZE / 2}
                                r={RADIUS}
                                fill='none'
                                strokeWidth={STROKE}
                                strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                                strokeDashoffset={-offset}
                                className={cn('transition-[stroke-dasharray] duration-500', segment.stroke)}
                            />
                        )

                        offset += length

                        return circle
                    })}
                </svg>

                <div className='absolute inset-0 flex flex-col items-center justify-center'>
                    <span className='text-[1.65rem] leading-none font-bold tracking-tight text-foreground'>
                        {centerValue}
                    </span>

                    <span className='mt-1 text-xs font-medium text-muted-foreground'>
                        {centerLabel}
                    </span>
                </div>
            </div>

            <ul className='grid w-full grid-cols-2 gap-2'>
                {segments.map(segment => (
                    <li key={segment.key} className='flex min-w-0 items-center gap-2 rounded-lg border bg-muted/30 px-2.5 py-2 text-xs last:col-span-2'>
                        <span className={cn('size-2 shrink-0 rounded-full', segment.fill)} aria-hidden='true' />

                        <span className='min-w-0 flex-1 truncate text-muted-foreground'>
                            {segment.label}
                        </span>

                        <span className='font-bold text-foreground'>
                            {segment.value}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    )
}
